import React, { useMemo } from "react";
import { useTranslations } from 'next-intl';
import { Phone, Mail, MapPin, Copy } from 'lucide-react';
import { LeafletMap } from '@/components/ui/map-component';
import { toast } from 'sonner';

export const ContactInfoSection: React.FC = () => {
  const t = useTranslations('Contact');

  // Function to copy text to clipboard and show toast notification
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('copiedSuccess', { label }), {
        duration: 3000,
        action: {
          label: '✓',
          onClick: () => {},
        },
      });

      (window as any).dataLayer.push({
        event: "contact_info_copy",
        source: window.location.href
      });
      
      console.log("copy sucsess");

    } catch (err) {
      toast.error(t('copiedError', { label }), {
        duration: 3000,
        action: {
          label: '✕',
          onClick: () => {},
        },
      });
    }
  };

  // Use useMemo to ensure markers are recreated when translations change
  const additionalMarkers = useMemo(() => [
  ], [t]);

  return (
    <section className="relative w-full py-12 md:py-16 lg:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        
        {/* Section Title */}
        <div className="text-left mb-8 md:mb-10">
          <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-x-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)]">
            {t('sectionTitle')}
          </h2>
        </div>

        {/* Content Grid - Using flex for equal height */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          
          {/* Left Side - Contact Information */}
          <div className="flex flex-col space-y-4 lg:w-2/5">
            
            {/* Phone Contact */}
            <div 
              className="flex bg-white p-5 md:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_25px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-0.5 flex-grow cursor-pointer group"
              onClick={() => copyToClipboard(t('contacts.value'), t('contacts.title'))}
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-brand-gradient rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Phone size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900 text-lg font-semibold mb-1 flex items-center gap-2">
                    {t('contacts.title')}
                    <Copy size={16} className="text-gray-400 group-hover:text-brand-secondary transition-colors duration-300" />
                  </h3>
                  <p className="text-gray-600 text-base group-hover:text-gray-900 transition-colors duration-300">
                    {t('contacts.value')}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Contact */}
            <div 
              className="flex bg-white p-5 md:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_25px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-0.5 flex-grow cursor-pointer group"
              onClick={() => copyToClipboard(t('email.value'), t('email.title'))}
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-brand-gradient rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <Mail size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900 text-lg font-semibold mb-1 flex items-center gap-2">
                    {t('email.title')}
                    <Copy size={16} className="text-gray-400 group-hover:text-brand-secondary transition-colors duration-300" />
                  </h3>
                  <p className="text-gray-600 text-base group-hover:text-gray-900 transition-colors duration-300">
                    {t('email.value')}
                  </p>
                </div>
              </div>
            </div>

            {/* Location Contact */}
            <div 
              className="flex bg-white p-5 md:p-6 shadow-[0_4px_20px_rgba(0,0,0,0.1)] hover:shadow-[0_6px_25px_rgba(0,0,0,0.12)] transition-all duration-300 hover:-translate-y-0.5 flex-grow cursor-pointer group"
              onClick={() => copyToClipboard(t('location.value'), t('location.title'))}
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-brand-gradient rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-gray-900 text-lg font-semibold mb-1 flex items-center gap-2">
                    {t('location.title')}
                    <Copy size={16} className="text-gray-400 group-hover:text-brand-secondary transition-colors duration-300" />
                  </h3>
                  <p className="text-gray-600 text-base group-hover:text-gray-900 transition-colors duration-300">
                    {t('location.value')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Enhanced Free Leaflet Map with Gradient Markers */}
          <div className="lg:w-3/5 flex items-stretch">
            <div className="rounded-xl w-full h-full overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.1)] relative z-0">
              <LeafletMap 
                latitude={51.5386342} 
                longitude={0.0517875} 
                zoom={15}
                markerTitle={t('markers.mainOffice')}
                markerColor="#08264C"
                darkMode={false}
                markers={additionalMarkers}
                glassmorphism={false}
                useGradientMarkers={true}
                enableGoogleMapsRedirect={true}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};